import { ExtendedUser } from '../Types/ExtendedUser';
import {
  ActiveCsas as ActiveCsas,
  CSAItem,
  MetricsCSAData,
  Stats,
  StatsCoachingNote,
  StatsContactReview,
} from '../Types/Metric';
import { FormSaveType } from './Form/formData.constants';
import DateCompare from '../Types/DateCompare';
import { logger } from './Logger';

export const displayPrr = (metric: Record<string, any>): number | string => {
  try {
    return metric.HMD.positiveResponseCount +
      metric.HMD.negativeResponseCount ===
      0
      ? -1
      : (metric.HMD.positiveResponseRate * 100).toFixed(2);
  } catch (e) {
    return -1;
  }
};

export const displayRap = (metric: Record<string, any>): number | string => {
  try {
    return metric.RAP.positiveResponseCount +
      metric.RAP.negativeResponseCount ===
      0
      ? -1
      : (metric.RAP.adjustedPositiveResponseRate * 100).toFixed(2);
  } catch (e) {
    return -1;
  }
};

export const customColorScale = ['#227D74', '#C3E9E7'];

export function countCsasWithCoaching(
  directReports: ExtendedUser[],
  stats: Stats
): number {
  const uniqueRecipients = new Set<string>();
  stats.coachingNotes.forEach((note: StatsCoachingNote) => {
    if (
      directReports.find(
        directReport => directReport.userName === note.recipient.userName
      )
    ) {
      uniqueRecipients.add(note.recipient.userName);
    }
  });

  return uniqueRecipients.size;
}

export function countCsasWithContactReviews(
  directReports: ExtendedUser[],
  stats: Stats
): number {
  const usersWithReviews = new Set<string>();

  stats.contactReviews.forEach((review: StatsContactReview) => {
    const isDirectReport = directReports.some(
      directReport => directReport.alias === review.agentUserName
    );
    const isDeliveredOrReviewed =
      review.contactStatus === FormSaveType.DELIVERED ||
      review.contactStatus === FormSaveType.REVIEWED;

    if (isDirectReport && isDeliveredOrReviewed) {
      usersWithReviews.add(review.agentUserID);
    }
  });

  return usersWithReviews.size;
}

export function countContactReviewReadByCSA(
  directReports: ExtendedUser[],
  stats: Stats
): number {
  return stats.contactReviews.filter((review: StatsContactReview) => {
    const isForDirectReport = directReports.some(
      directReport => directReport.alias === review.agentUserName
    );
    return isForDirectReport && review.contactStatus === FormSaveType.REVIEWED;
  }).length;
}

export function getDirectReportsWithDeliveredCoachingNotes(
  directReports: ExtendedUser[],
  stats: Stats
): ExtendedUser[] {
  return directReports.filter(directReport =>
    stats.coachingNotes.find(
      note => directReport.userName === note.recipient.userName
    )
  );
}

export function getNonDistinctDirectReportsWithDeliveredCoachingNotes(
  directReports: ExtendedUser[],
  stats: Stats
): ExtendedUser[] {
  return stats.coachingNotes.reduce((result: ExtendedUser[], note) => {
    const matchingDirectReport = directReports.find(
      directReport => directReport.userName === note.recipient.userName
    );

    if (matchingDirectReport) {
      result.push(matchingDirectReport);
    }

    return result;
  }, []);
}

export function getActiveDirectReports(
  directReports: ExtendedUser[],
  stats: Stats,
  activeCsasUserIds: string[]
): ExtendedUser[] {
  const activeUserIds = activeCsasUserIds.map(activeUser =>
    activeUser.split(':').pop()
  );

  return directReports.filter(directReport =>
    activeUserIds.includes(directReport.userID)
  );
}

export function getActiveDirectReportsWithTwiceDeliveredCoachingNotes(
  directReports: ExtendedUser[],
  stats: Stats,
  activeCsasUserIds: string[]
): ExtendedUser[] {
  const activeUserIds = activeCsasUserIds.map(activeUser =>
    activeUser.split(':').pop()
  );

  return directReports.filter(directReport => {
    const isActive = activeUserIds.includes(directReport.userID);
    if (!isActive) {
      return false;
    }

    const coachingNotesCount = stats.coachingNotes.filter(
      note => directReport.userName === note.recipient.userName
    ).length;

    return coachingNotesCount >= 2;
  });
}

export function getDirectReportsWithDeliveredContactReviews(
  directReports: ExtendedUser[],
  stats: Stats
): ExtendedUser[] {
  return directReports.filter(directReport =>
    stats.contactReviews.some(
      review =>
        review.agentUserName === directReport.alias &&
        (review.contactStatus === FormSaveType.DELIVERED ||
          review.contactStatus === FormSaveType.REVIEWED)
    )
  );
}

export function getDiffDirectReports(
  allDirectReports: ExtendedUser[],
  excludedDirectReports: ExtendedUser[]
): ExtendedUser[] {
  return allDirectReports.filter(includedDirectReport =>
    excludedDirectReports.every(
      excludedDirectReport =>
        includedDirectReport.alias !== excludedDirectReport.alias
    )
  );
}

// get all direct reports that have at least one DELIVERED status
export function getDirectReportsWithoutContactReviewsUnopened(
  directReports: ExtendedUser[],
  stats: Stats
): ExtendedUser[] {
  return directReports.filter(directReport =>
    stats.contactReviews.some(
      review =>
        review.agentUserName == directReport.alias &&
        review.contactStatus == FormSaveType.DELIVERED
    )
  );
}

export function getDateRange(dateSelection: string): {
  startDate: string;
  endDate: string;
} {
  const endDate = new Date();
  let startDate = new Date();

  switch (dateSelection) {
    case DateCompare.DAY:
      startDate.setDate(endDate.getDate() - 15);
      break;
    case DateCompare.WEEK:
      startDate.setDate(endDate.getDate() - 56);
      break;
    case DateCompare.MONTH:
      startDate.setMonth(endDate.getMonth() - 2);
      break;
    default:
      logger.error(`date val:${dateSelection}`);
      throw new Error('Invalid date selection');
  }

  return { startDate: formatDate(startDate), endDate: formatDate(endDate) };
}

export function getMetricDateValues(dateSelection: string): string[] {
  if (dateSelection === DateCompare.DAY) {
    const dates: string[] = [];
    const today = new Date();

    // go from yesterday back 15 days
    for (let i = 1; i < 16; i++) {
      const pastDate = new Date();
      pastDate.setDate(today.getDate() - i);

      const year: number = pastDate.getFullYear();
      const month: string = String(pastDate.getMonth() + 1).padStart(2, '0');
      const day: string = String(pastDate.getDate()).padStart(2, '0');

      dates.push(`${year}-${month}-${day}`);
    }

    return dates;
  }

  return [];
}

function formatDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export const directReport = 'DIRECT_REPORT';
